import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download } from 'lucide-react';
import { getToken } from '@/lib/auth';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get('/admin/orders').then(res => setOrders(res.data)).catch(console.error);
  }, []);

  const handleStatusChange = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success('Order status updated.');
      api.get('/admin/orders').then(res => setOrders(res.data));
    } catch (err) {
      toast.error('Failed to update order.');
    }
  };

  const handleExport = () => {
    const token = getToken();
    fetch(`${API_BASE}/admin/export/orders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders_export.csv';
      a.click();
      URL.revokeObjectURL(url);
    }).catch(console.error);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
  const statusColors = {
    pending: 'border-[rgba(201,168,76,0.3)] text-[hsl(var(--gold))]',
    paid: 'border-[rgba(122,158,126,0.4)] text-[hsl(var(--sage))]',
    shipped: 'border-[rgba(168,181,178,0.3)] text-[hsl(var(--silver))]',
    canceled: 'border-[rgba(185,105,122,0.3)] text-[hsl(var(--rose))]',
  };

  return (
    <AdminShell title="Orders">
      <div className="flex items-center justify-between mb-6">
        <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
          {orders.length} order{orders.length !== 1 ? 's' : ''}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
        <Table data-testid="admin-orders-table">
          <TableHeader>
            <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Customer</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Product</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Total</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Date</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] py-8">No orders yet.</TableCell></TableRow>
            ) : orders.map(order => (
              <TableRow key={order._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                <TableCell>
                  <p className="font-['Inter'] text-sm text-[hsl(var(--foreground))]">{order.customer_name}</p>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))]">{order.customer_email}</p>
                </TableCell>
                <TableCell className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{order.product_name}</TableCell>
                <TableCell className="font-['Inter'] text-sm text-[hsl(var(--foreground))]">${order.total?.toFixed(2)}</TableCell>
                <TableCell className="font-['Inter'] text-xs text-[hsl(var(--silver))]">{formatDate(order.created_at)}</TableCell>
                <TableCell>
                  <Select value={order.status} onValueChange={(v) => handleStatusChange(order._id, v)}>
                    <SelectTrigger className="w-[120px] h-8 bg-transparent border-[rgba(201,168,76,0.15)] text-[hsl(var(--foreground))] font-['Inter'] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)]">
                      {['pending', 'paid', 'shipped', 'canceled'].map(s => (
                        <SelectItem key={s} value={s} className="text-[hsl(var(--foreground))] font-['Inter'] text-sm capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminShell>
  );
}
